import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Input, Label, TextField } from '@heroui/react';
import { DatePickerField } from '@/components';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { JournalLineRow, TotalsBar } from './components';
import type { JournalEntryFormModel } from './useJournalEntryForm';

type JournalEntryFormViewProps = {
    form: JournalEntryFormModel;
    /** Optional cancel affordance (shown when hosted in a modal). */
    onCancel?: () => void;
};

const POST_REASON_ID = 'journal-post-reason';

export const JournalEntryFormView: React.FC<JournalEntryFormViewProps> = ({
    form,
    onCancel,
}) => {
    const { t } = useTranslation('pages');

    const feedbackText = React.useMemo(() => {
        if (!form.feedback) {
            return null;
        }
        if (form.feedback.kind === 'saved') {
            return t('journalEntry.feedback.saved');
        }
        return form.feedback.entryNumber
            ? t('journalEntry.feedback.posted', {
                  number: form.feedback.entryNumber,
              })
            : t('journalEntry.feedback.postedPending');
    }, [form.feedback, t]);

    return (
        <div
            className="flex flex-col gap-6"
            {...getNodeTestIdProp('journal-entry-form')}
        >
            {form.isEditingPosted ? (
                <div
                    className="flex items-center gap-2 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-soft-foreground"
                    {...getNodeTestIdProp('journal-entry-posted-note')}
                >
                    <Icon aria-hidden icon="gravity-ui:lock" />
                    {t('journalEntry.postedImmutable')}
                </div>
            ) : null}

            {/* Header fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="flex flex-col gap-1">
                    <DatePickerField
                        id="journal-entry-date"
                        isDisabled={form.isEditingPosted}
                        label={t('journalEntry.fields.date')}
                        name="journal-entry-date"
                        onChange={form.onDateChange}
                        outputFormat="YYYY-MM-DD"
                        value={form.date}
                    />
                    {form.periodClosed ? (
                        <span
                            className="flex items-center gap-1 text-xs text-warning-soft-foreground"
                            {...getNodeTestIdProp(
                                'journal-entry-period-warning',
                            )}
                        >
                            <Icon
                                aria-hidden
                                icon="gravity-ui:triangle-exclamation"
                            />
                            {t('journalEntry.periodClosedWarning', {
                                period: form.periodStatus?.periodLabel ?? '',
                            })}
                        </span>
                    ) : null}
                </div>

                <TextField
                    isDisabled={form.isEditingPosted}
                    onChange={form.onDescriptionChange}
                    value={form.description}
                    {...getNodeTestIdProp('journal-entry-description')}
                >
                    <Label>{t('journalEntry.fields.description')}</Label>
                    <Input
                        placeholder={t(
                            'journalEntry.fields.descriptionPlaceholder',
                        )}
                    />
                </TextField>

                <TextField
                    isDisabled={form.isEditingPosted}
                    onChange={form.onDocumentRefChange}
                    value={form.documentRef}
                    {...getNodeTestIdProp('journal-entry-document-ref')}
                >
                    <Label>{t('journalEntry.fields.documentRef')}</Label>
                    <Input
                        placeholder={t(
                            'journalEntry.fields.documentRefPlaceholder',
                        )}
                    />
                </TextField>
            </div>

            {/* Lines editor */}
            <div className="flex flex-col gap-3">
                <div className="hidden grid-cols-[minmax(0,1fr)_7rem_7rem_minmax(0,10rem)_auto] gap-3 px-1 text-xs font-medium uppercase tracking-wide text-muted sm:grid">
                    <span>{t('journalEntry.columns.account')}</span>
                    <span className="text-right">
                        {t('journalEntry.columns.debit')}
                    </span>
                    <span className="text-right">
                        {t('journalEntry.columns.credit')}
                    </span>
                    <span>{t('journalEntry.columns.memo')}</span>
                    <span className="sr-only">
                        {t('journalEntry.columns.actions')}
                    </span>
                </div>

                <div className="flex flex-col gap-3 sm:gap-2">
                    {form.lines.map((line, index) => (
                        <JournalLineRow
                            form={form}
                            index={index}
                            key={line.key}
                        />
                    ))}
                </div>

                <div>
                    <Button
                        isDisabled={form.isEditingPosted}
                        onPress={form.onAddLine}
                        size="sm"
                        variant="tertiary"
                        {...getNodeTestIdProp('journal-entry-add-line')}
                    >
                        <Icon aria-hidden icon="gravity-ui:plus" />
                        {t('journalEntry.addLine')}
                    </Button>
                </div>
            </div>

            <TotalsBar
                balanced={form.balanced}
                differenceGel={form.differenceGel}
                hasAmounts={form.hasAmounts}
                totalCredit={form.totalCredit}
                totalDebit={form.totalDebit}
            />

            {/* Feedback / errors */}
            {feedbackText ? (
                <div
                    className="flex items-center justify-between gap-3 rounded-lg bg-success-soft px-3 py-2 text-sm text-success-soft-foreground"
                    {...getNodeTestIdProp('journal-entry-feedback')}
                >
                    <span className="flex items-center gap-2">
                        <Icon aria-hidden icon="gravity-ui:circle-check" />
                        {feedbackText}
                    </span>
                    <Button
                        onPress={form.onStartAnother}
                        size="sm"
                        variant="ghost"
                        {...getNodeTestIdProp('journal-entry-start-another')}
                    >
                        {t('journalEntry.startAnother')}
                    </Button>
                </div>
            ) : null}

            {form.saveError ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('journal-entry-error')}
                >
                    {form.saveError}
                </p>
            ) : null}

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-end gap-3">
                {/* Disabled-reason: a polite live region so it is announced when
                    it changes (a disabled Post button isn't focusable, so
                    `aria-describedby` alone wouldn't be read). Always rendered as
                    a stable region; `aria-describedby` on Post points here too. */}
                <p
                    aria-live="polite"
                    className="mr-auto text-sm text-muted empty:hidden"
                    id={POST_REASON_ID}
                    {...getNodeTestIdProp('journal-entry-post-reason')}
                >
                    {form.postReason}
                </p>

                {onCancel ? (
                    <Button
                        isDisabled={form.isBusy}
                        onPress={onCancel}
                        variant="tertiary"
                        {...getNodeTestIdProp('journal-entry-cancel')}
                    >
                        {t('journalEntry.actions.cancel')}
                    </Button>
                ) : null}

                <Button
                    isDisabled={!form.canSaveDraft}
                    isPending={form.isSaving}
                    onPress={form.onSaveDraft}
                    variant="outline"
                    {...getNodeTestIdProp('journal-entry-save-draft')}
                >
                    {t('journalEntry.actions.saveDraft')}
                </Button>

                <Button
                    aria-describedby={
                        form.postReason ? POST_REASON_ID : undefined
                    }
                    isDisabled={!form.canPost}
                    isPending={form.isPosting}
                    onPress={form.onPost}
                    variant="primary"
                    {...getNodeTestIdProp('journal-entry-post')}
                >
                    {t('journalEntry.actions.post')}
                </Button>
            </div>
        </div>
    );
};
